import React from 'react';
import Downshift from 'downshift';

import { NODE_TYPE as NT } from 'shared/constants/slate';
import { keycodeFromEvent } from 'shared/helpers';
import { classes as cn } from 'shared/styles';

import {
  BASE_COLOR as BC
} from 'shared/constants/colors';

import { SvgIcon } from 'components/elems';
import { cx } from 'helpers';
import { makeNodeProps } from '../utils';



const MENU_ITEM_HEIGHT = 30;  // px


// https://picsvg.com
// https://icons8.com

const IconFrame = ({ padding, children }) => (
  <span
    className={cx('flex', cn.svgFillCurrentColor)}
    style={{ padding }}
  >
    {children}
  </span>
);


const InitReceiver = (opts = {}) => (change, editor) => {
  const { type } = opts;
  if (!type) return;

  const { startOffset } = change.value;

  const nodeProps = makeNodeProps({
    type,
    isVoid: false,
  }, {
    src: null,
    isReceiving: true,
    error: null,
  });

  return editor.onChange(
    change
      .setBlock(nodeProps)
      .moveOffsetsTo(0, startOffset)
      .delete()
  );
}


const commands = [
  {
    title: 'Bulleted List',
    nodeType: NT.ul,
    icon: (
      <IconFrame padding='2px'>
        <SvgIcon.ooff.BulletedList size={16} />
      </IconFrame>
    ),
  },
  {
    title: 'Numbered List',
    nodeType: NT.ol,
    icon: (
      <IconFrame padding='2px'>
        <SvgIcon.ooff.NumberedList size={16} />
      </IconFrame>
    ),
  },
  {
    title: 'Header',
    nodeType: NT.heading,
    icon: (
      <IconFrame padding='4px'>
        <SvgIcon.fa.Header size={12} />
      </IconFrame>
    ),
    onSelect: (change, editor) => {
      const { startOffset } = change.value;
      const nodeProps = makeNodeProps({ type: NT.heading });

      return editor.onChange(
        change
          .setBlock(nodeProps)
          .moveOffsetsTo(0, startOffset)
          .delete()
      );
    },
  },
  {
    title: 'Quote',
    nodeType: NT.blockquote,
    icon: (
      <IconFrame padding='1px'>
        <SvgIcon.md.Quote size={18} />
      </IconFrame>
    ),
  },
  {
    title: 'Code Snippet',
    nodeType: NT.codeBlock,
    icon: (
      <IconFrame padding='4px'>
        <SvgIcon.ooff.CodeSnippet size={12} />
      </IconFrame>
    ),
  },
  {
    title: 'Image',
    nodeType: NT.image,
    icon: (
      <IconFrame padding='3px'>
        <SvgIcon.md.Image size={14} />
      </IconFrame>
    ),
    onSelect: InitReceiver({ type: NT.image }),
  },
  {
    title: 'Embed',
    nodeType: NT.embed,
    icon: (
      <IconFrame padding='3px'>
        <SvgIcon.md.Link size={14} />
      </IconFrame>
    ),
    onSelect: InitReceiver({ type: NT.embed }),
  },
];


const CommandItem = ({ item, isHighlighted, ...props }) => {
  const { title, icon } = item;

  return (
    <div
      className="pointer ph2 color-animate bg-animate flex items-center"
      style={{
        background: isHighlighted ? BC.nearWhite : BC.white,
        height: `${MENU_ITEM_HEIGHT}px`,
      }}
      {...props}
    >
      <div
        className="flex br-100 ba b--moon-gray mr2"
        style={{ borderColor: isHighlighted ? BC.midGray : BC.moonGray }}
      >
        {icon}
      </div>
      <div className="fw4 f6 black-70">
        {title}
      </div>
    </div>
  );
};

const MenuHint = () => (
  <div
    className={cx(
      "fw3 ph2 pb1 light-silver",
      "pointer-events-none",
    )}
    style={{ fontSize: '11px' }}
  >
    Type to filter commands
  </div>
);


const NoResults = () => (
  <div
    className={cx(
      "flex items-center justify-start ph2",
      "ttu f7 fw4 light-silver",
      "pointer-events-none",
    )}
    style={{ height: `${MENU_ITEM_HEIGHT}px` }}
  >
    No results
  </div>
);


export class CommandMenu extends React.Component {
  constructor(props) {
    super(props);

    // upstream binding
    props.wire.onKeyDown = this.onKeyDown.bind(this);

    this.editor = props.wire.editor;
  }

  onKeyDown = (event) => {
    if (!this.downshift) return;

    const { code, kc } = keycodeFromEvent(event);

    if (code === kc.codes.down) return this.downshift.moveHighlightedIndex(1);
    if (code === kc.codes.up) return this.downshift.moveHighlightedIndex(-1);

    if (code === kc.codes.esc) {
      // don't let autotypeahead onchange kick in.
      this.props.wire.halt = true;

      this.editor.props.typeahead.off();
      this.props.wire.change.focus();
      return;
    }

    if (code === kc.codes.enter) {
      // For some reason, selectHighlightedItem() doesn't do anything..
      // so manually call handleSelection with item.
      const { highlightedIndex } = this.downshift.state;
      const item = this.downshift.items[highlightedIndex];
      if (!item) {
        return;
      }

      this.handleSelection(item);

      // Return non-null to prohibit slate after plugin from kicking in.
      // Ref: https://github.com/ianstormtaylor/slate/issues/1345
      return false;
    }

    if (code === kc.codes.tab) {
      return (event.shiftKey
        ? this.downshift.moveHighlightedIndex(-1)
        : this.downshift.moveHighlightedIndex(1)
      );
    }
  }

  handleOuterClick = () => {
    this.props.wire.halt = true;
    this.editor.props.typeahead.off();
  }

  getFilteredItems = () => {
    const { inputValue } = this.props;

    return commands.filter(c =>
      c.title.toLowerCase().includes(inputValue.toLowerCase())
    );
  }

  handleSelection = (item) => {
    const change = this.props.wire.change;
    const editor = this.editor;
    const { nodeType, onSelect } = item;

    // if onSelect is defined, call it with slate change.
    if (onSelect) return onSelect(change, editor);

    // .. otherwise, just setBlock for nodeType.
    // (setBlock first then delete.. the order seems to matter for slate.)
    const { startOffset } = change.value;
    return editor.onChange(
      change
        .setBlock(nodeType)
        .moveOffsetsTo(0, startOffset)
        .delete()
    );
  }

  render() {
    const { onChange, inputValue } = this.props;

    return (
      <Downshift
        ref={ref => this.downshift = ref}
        defaultIsOpen
        defaultHighlightedIndex={0}
        inputValue={inputValue}
        onChange={onChange}
        onOuterClick={this.handleOuterClick}
        itemToString={i => i == null ? '' : i.nodeType}
        onSelect={this.handleSelection}
        render={({
          getItemProps,
          isOpen,
          highlightedIndex,
        }) => {
          if (!isOpen) return null;

          const filteredCommands =
            this.getFilteredItems().map((item, index) => (
              <CommandItem
                {...getItemProps({item})}
                isHighlighted={index === highlightedIndex}
                item={item}
                key={item.title}
              />
            ));

          const hasResult = filteredCommands.length > 0;

          return (
            // Downshift cares whether a root component is
            // a custom component.. just use div.
            <div className={cx(
              "moa-ff-sans black-80 pv1 br2 overflow-hidden",
              "-popover-frame",
              "shadow-normal",  // override -popover-frame
            )}>
              { filteredCommands.length > 1 ? <MenuHint /> : null }
              { hasResult ? filteredCommands : <NoResults /> }
            </div>
          );
        }}
      />
    );
  }
}

CommandMenu.defaultProps = {
  wire: {},
};



// WEBPACK FOOTER //
// ./src/components/slate/decors/CommandMenu.js